import React, { useCallback } from 'react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { usePostMessageRouter, type PostMessageHandler } from '../../hooks/usePostMessageRouter'
import { getGravityUserPlan, getGravityUserRegion, getGravityUserId, getGravityUserStatus, useAuthenticatedFetch, useDemoStatus } from '../../hooks/useAuth'
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/authStore';
import { toast } from 'sonner';
import {
  OnboardingStatusService,
  type OnboardingStatusData,
} from '../../domains/user/services/OnboardingStatusService';
import { CreatelyApiClient } from '../../lib/creately/CreatelyApiClient';
import { getCookie } from '../../lib/cookies';
import { getGravityTokenClaimsFromToken } from '../../domains/auth/utils/gravityTokenClaims';
import { Region } from '../../core/types';
import { Logger } from '@/utils/logger';
import { Tracker } from '../../domains/analytics/services/Tracker';
import { useOnboardingContext } from '../../contexts/OnboardingContext';
import { buildLocalizedSiteUrl } from '../../i18n/languagePreference';
import { useI18nLocale } from '../../hooks/useTranslation';
import { demoDiagramFromUrl, demoCheckoutReturnUrl, rememberDemoContinuation, pendingDemoContinuation, resolveDemoContinuationFolder } from '../../dashboard/utils/demoContinuation';
import { buildFolderPath } from '../../dashboard/hooks/useDashboardNavigate';
import { Button } from '../ui/button';
import { isDevopsPreviewHost } from './devopsPreviewAuth';
import { folderFirstOnboardingDestination, isFolderFirstOnboardingEntry } from '../../dashboard/utils/folderFirstOnboarding';
import {
  DEMO_STATUS,
  setOnboardingDialogBlocksChrome,
  shouldBlockStartupChromeForOnboardingDialog,
} from './onboardingDialogChromeGate';

const logger = Logger.getLoggerWithPrefix('OnboardingDialog');

export enum Pages {
  SignIn = 'signin-in-app',
  SignUp = 'signup-in-app/?app=1',
  Plans = 'plans-in-app/?app=1',
  Onboarding = 'onboarding-in-app',
  DemoEmailVerify = 'demo/email/verify/?app=1',
  PurchaseStarted = 'purchase-started',
}

enum ReceivePostMessageEvents {
  SIGN_IN_SUCCESS = 'phoenix:signInSuccess',
  SIGN_UP_SUCCESS = 'phoenix:signUpSuccess',
  SIGN_UP_SIZE_CHANGE = 'phoenix:signUpSizeChange',
  EMAIL_VERIFY_SUCCESS = 'phoenix:emailVerifySuccess',
  PURCHASE_STARTED = 'phoenix:purchaseStarted',
  DEMO_EMAIL_CONTINUE = 'phoenix:demoEmailContinue',
  CONTINUE_FREE = 'phoenix:continueFree',
  SIGN_UP_CLOSE = 'phoenix:signUpClose',
  SIGN_UP_FAIL = 'phoenix:signUpFail',
  RELOAD_APP = 'phoenix:reloadApp',
  ONBOARDING_SUCCESS = 'phoenix:onboardingSuccess',
  ONBOARDING_ERROR = 'phoenix:onboardingError',
  TRANSFER_DEMO_DIAGRAM = 'phoenix:transferDemoDiagram',
  GRAVITY_TOKEN = 'phoenix:gravityToken',
}

const CHECK_INTERVAL_MS = 5000
const ORG_DEMO_SIGNUP_DELAY_MS = 120000
const siteUrl = import.meta.env.VITE_SITE_URL
const baseUrl = import.meta.env.VITE_BASE_URL

export const OPEN_ONBOARDING_EVENT = 'creately:open-onboarding'
const SHARE_LINK_ONBOARDING_SESSION_KEY = 'creately:share-link-onboarding'

function getDemoDiagramTypeQuery(): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  const storedType = window.sessionStorage.getItem('diagramType')
  const diagramTypeMap: Record<string, string> = {
    genogram: 'genogram',
    flowchart: 'flowchart',
    org: 'orgchart',
  }
  const mappedType = storedType ? diagramTypeMap[storedType] : undefined
  if (!mappedType) {
    return null
  }

  return new URLSearchParams({ type: mappedType }).toString()
}

function hasShareLinkLoginContext(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.get('lt')) {
      window.sessionStorage.setItem(SHARE_LINK_ONBOARDING_SESSION_KEY, 'true')
      return true
    }

    return window.sessionStorage.getItem(SHARE_LINK_ONBOARDING_SESSION_KEY) === 'true'
  } catch {
    return false
  }
}

function isOrgDemoDiagram(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return window.sessionStorage.getItem('diagramType') === 'org'
}

function shouldDelayOrgDemoSignup(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return isOrgDemoDiagram() && window.sessionStorage.getItem('diagramTag') === 'ABCD-EFGG'
}

type EmailVerificationPayload = {
  plan?: string
}

type OnboardingStatusSnapshot = {
  onboardingProgress?: string;
  onboardingLastUpdated?: string;
} | null

type OnboardingPurpose = 'work' | 'school' | 'personal' | 'exploring' | 'skipped'

type PhoenixOnboardingSuccessPayload = {
  usage?: string;
  industry?: string;
  role?: string;
  purpose?: string;
  intentPurpose?: string;
  onboardingProgress?: string;
}

function isOnboardingPurpose(value: unknown): value is OnboardingPurpose {
  return value === 'work'
    || value === 'school'
    || value === 'personal'
    || value === 'exploring'
    || value === 'skipped'
}

function resolveOnboardingPurpose(payload: PhoenixOnboardingSuccessPayload): OnboardingPurpose {
  if (isOnboardingPurpose(payload.intentPurpose)) {
    return payload.intentPurpose
  }

  if (isOnboardingPurpose(payload.purpose)) {
    return payload.purpose
  }

  switch (payload.usage) {
    case 'work':
      return 'work'
    case 'education':
      return 'school'
    case 'personal':
      return 'personal'
    default:
      return 'skipped'
  }
}

interface OnboardingDialogProps {
  externalOnly?: boolean;
}

export const OnboardingDialog: React.FC<OnboardingDialogProps> = ({ externalOnly = false }) => {
  const onboarding = useOnboardingContext();
  // The dashboard consumes this query after opening the folder picker. Keep the
  // entry decision stable while the demo status resolves asynchronously.
  const enteredFolderFirstOnboarding = React.useRef(
    typeof window !== 'undefined' && isFolderFirstOnboardingEntry(new URL(window.location.href)),
  ).current;
  const hasOrgOptionsQuery =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('orgOptions') === 'true';

  const status = useDemoStatus(CHECK_INTERVAL_MS)
  const [open, setOpen] = React.useState(false)
  const [dismissed, setDismissed] = React.useState(false)
  const [page, setPage] = React.useState<Pages>(Pages.DemoEmailVerify)
  const [fromUpgrade, setFromUpgrade] = React.useState<boolean>(false)
  const [isCompact, setIsCompact] = React.useState(true)
  const [contentSize, setContentSize] = React.useState<{ width?: number | string; height?: number | string }>({})
  const [demoDiagramTypeQuery, setDemoDiagramTypeQuery] = React.useState<string | null>(() => getDemoDiagramTypeQuery())
  const [isShareLinkLoginContext] = React.useState(() => hasShareLinkLoginContext())
  const [hasReceivedGravityToken, setHasReceivedGravityToken] = React.useState(false)
  /** Bumps iframe URL so sign-in reloads to the email step (same outcome as Phoenix email-row clear). */
  const [signInFrameNonce, setSignInFrameNonce] = React.useState(0)
  const locale = useI18nLocale()
  const iframeRef = React.useRef<HTMLIFrameElement>(null!)
  const prevOpenRef = React.useRef(false)
  const initialUserStatus = React.useRef(status);
  const externalTriggerRef = React.useRef(false)

  const gravityToken = getCookie('gravity_token');
  const initialGravityToken = React.useRef<string | null>(gravityToken ?? null);
  const hasTransferredDemoDiagramRef = React.useRef(false);
  const transferInFlight = React.useRef<Promise<void> | null>(null);
  const pendingPreviewSignInRef = React.useRef(false);
  const demoDiagramId = React.useRef<string | null>(null);
  const [recovery, setRecovery] = React.useState<{ retry: () => void } | null>(null);
  const [preparingWork, setPreparingWork] = React.useState(false);
  const authenticatedFetch = useAuthenticatedFetch();
  // Capture only a demo-owned editor journey, never a shared-link login.
  if (initialUserStatus.current === DEMO_STATUS && !isShareLinkLoginContext) {
    demoDiagramId.current = demoDiagramFromUrl(new URL(window.location.href)) ?? demoDiagramId.current;
  }

  // Don't show onboarding dialog if app is embedded in an iframe
  const isEmbedded = React.useMemo(() => {
    if (fromUpgrade) return false;
    if (typeof window === 'undefined') return false;
    return window.self !== window.top;
  }, [fromUpgrade]);

  const anonymousAccess =
    typeof window !== 'undefined' && localStorage.getItem('isAnonymousAccess') === 'true';

  // Publish before DiagramEditor's useEffect so blank-canvas shape picker waits
  // for the demo iframe (including the first paint before setOpen flushes).
  const blocksStartupChrome = shouldBlockStartupChromeForOnboardingDialog({
    dialogOpen: open,
    externalOnly,
    isEmbedded,
    hasOrgOptions: onboarding.orgOptions || hasOrgOptionsQuery,
    genoWizardActive: onboarding.genoWizard !== null,
    hierarchyChartWizardActive: onboarding.hierarchyChartWizard !== null,
    status,
    dismissed,
    anonymousAccess,
    delayOrgDemoSignup: shouldDelayOrgDemoSignup(),
  });

  React.useLayoutEffect(() => {
    setOnboardingDialogBlocksChrome(blocksStartupChrome);
    return () => {
      setOnboardingDialogBlocksChrome(false);
    };
  }, [blocksStartupChrome]);

  React.useEffect(() => {
    if (initialGravityToken.current || !gravityToken) {
      return;
    }
    initialGravityToken.current = gravityToken;
  }, [gravityToken]);

  const userRegion = getGravityUserRegion();

  const apiClient = CreatelyApiClient.fromEnv({
    CREATELY_API_BASE_URL: import.meta.env.VITE_CREATELY_API_BASE_URL,
    CREATELY_API_TIMEOUT: import.meta.env.VITE_CREATELY_API_TIMEOUT,
    CREATELY_API_RETRY_ATTEMPTS: import.meta.env.VITE_CREATELY_API_RETRY_ATTEMPTS,
    CREATELY_API_REGIONAL_BASE_URL: import.meta.env.VITE_CREATELY_API_REGIONAL_BASE_URL,
  }, Region[userRegion?.toUpperCase() as keyof typeof Region]);
  
  const onboardingService = React.useMemo(() => new OnboardingStatusService(apiClient), [apiClient]);
  const queryClient = useQueryClient();

  const syncSessionFromBrowser = React.useCallback(() => {
    queueMicrotask(() => {
      useAuthStore.getState().initializeAuth();
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    });
  }, [queryClient]);

  // Query to fetch onboarding status
  const { data: onboardingStatus } = useQuery({
    queryKey: ['onboardingStatus'],
    queryFn: async () => {
      if (!gravityToken) return null;
      return onboardingService.fetchOnboardingStatus(gravityToken);
    },
    // Only fetch when user is not demo and we have a token
    enabled: initialUserStatus.current !== DEMO_STATUS && !!gravityToken,
  });

  const { mutateAsync: transferDemoDiagram } = useMutation<void, Error, string>({
    mutationFn: async (gravityTokenToTransfer) => {
      const response = await fetch('/api/transferDemoUserDiagram', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({ gravityToken: gravityTokenToTransfer }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to transfer demo user diagram (${response.status})`);
      }
    },
  });

  const transferDemoDiagramIfNeeded = React.useCallback(async () => {
    if (hasTransferredDemoDiagramRef.current) {
      return;
    }

    if (initialUserStatus.current !== DEMO_STATUS) {
      return;
    }

    // The editor may have replaced /d/new since this dialog last rendered.
    if (!isShareLinkLoginContext) {
      demoDiagramId.current = demoDiagramFromUrl(new URL(window.location.href)) ?? demoDiagramId.current;
    }
    const gravityTokenToTransfer = initialGravityToken.current;
    if (!gravityTokenToTransfer) {
      return;
    }

    if (getGravityUserStatus() === DEMO_STATUS) throw new Error('Account session is not ready');
    if (!transferInFlight.current) {
      transferInFlight.current = transferDemoDiagram(gravityTokenToTransfer).then(async () => {
        const userId = getGravityUserId();
        const diagramId = demoDiagramId.current;
        if (userId && diagramId) {
          await queryClient.fetchQuery({
            queryKey: ['demo-continuation', userId, diagramId],
            staleTime: 0,
            queryFn: () => resolveDemoContinuationFolder(authenticatedFetch, diagramId, userId),
          });
          rememberDemoContinuation(userId, diagramId);
        }
        hasTransferredDemoDiagramRef.current = true;
      }).finally(() => { transferInFlight.current = null; });
    }
    await transferInFlight.current;
  }, [transferDemoDiagram, authenticatedFetch, queryClient, isShareLinkLoginContext]);

  const handleAuthSuccess = React.useCallback(async (onComplete?: () => void) => {
    setPreparingWork(true);
    setRecovery(null);
    try {
      await transferDemoDiagramIfNeeded();
      syncSessionFromBrowser();
      onComplete?.();
    } catch (error) {
      logger.error('Failed to transfer demo user diagram', error);
      setOpen(true);
      setRecovery({ retry: () => { void handleAuthSuccess(onComplete); } });
    } finally {
      setPreparingWork(false);
    }
  }, [transferDemoDiagramIfNeeded, syncSessionFromBrowser]);

  // Handle demo window
  React.useEffect(() => {
    if (externalOnly) return;
    if (enteredFolderFirstOnboarding) return;
    if (isEmbedded) return;
    if (onboarding.orgOptions || hasOrgOptionsQuery) return;
    if (onboarding.genoWizard !== null) return;
    if (onboarding.hierarchyChartWizard !== null) return;
    
    if (externalTriggerRef.current) {
      externalTriggerRef.current = false
      return
    }

    if (status !== DEMO_STATUS) {
      return
    }

    if (dismissed || anonymousAccess) {
      return
    }

    if (shouldDelayOrgDemoSignup()) {
      const timerId = window.setTimeout(() => {
        setDemoDiagramTypeQuery(getDemoDiagramTypeQuery())
        setOpen(true);
        setPage(Pages.DemoEmailVerify);
      }, ORG_DEMO_SIGNUP_DELAY_MS);

      return () => window.clearTimeout(timerId);
    }

    setDemoDiagramTypeQuery(getDemoDiagramTypeQuery())
    setOpen(true);
    setPage(Pages.DemoEmailVerify);
  }, [
    status,
    dismissed,
    isEmbedded,
    onboarding.genoWizard,
    onboarding.hierarchyChartWizard,
    onboarding.orgOptions,
    hasOrgOptionsQuery,
    externalOnly,
    enteredFolderFirstOnboarding,
    anonymousAccess,
  ]);

  // Handle onboarding window
  React.useEffect(() => {
    if (externalOnly) return;
    if (isEmbedded) return;
    if (onboarding.genoWizard !== null) return;
    if (onboarding.hierarchyChartWizard !== null) return;

    if (!onboardingStatus || initialUserStatus.current === DEMO_STATUS || anonymousAccess) return;

    const shouldShow = shouldShowOnboarding(onboardingStatus);

    if (shouldShow) {
      setOpen(true);
      setPage(Pages.Onboarding);
    }

    // exclude `status` from deps to avoid re-run when demo status updates
  }, [
    onboardingStatus,
    isEmbedded,
    onboarding.genoWizard,
    onboarding.hierarchyChartWizard,
    externalOnly,
    anonymousAccess,
  ])

  const shouldShowOnboarding = (status: OnboardingStatusSnapshot): boolean => {
    if (!status) return false;
    return !status.onboardingProgress;
  };

  React.useEffect(() => {
    setIsCompact(page !== Pages.Plans)
  }, [page])

  React.useEffect(() => {
    if (page === Pages.Onboarding) {
      setContentSize({})
    }
  }, [page])

  const setDemoUserSignIn = React.useCallback((isDemoUserSignIn: boolean) => {
    localStorage.setItem('demoUserSignIn', isDemoUserSignIn.toString())
  }, [])

  const checkDemoUserSignIn = React.useCallback(() => {
    return localStorage.getItem('demoUserSignIn') === 'true'
  }, [])

  const effectivePage = React.useMemo(() => {
    if (page === Pages.DemoEmailVerify && checkDemoUserSignIn()) {
      setDemoUserSignIn(false)
      return Pages.SignIn
    }
    return page
  }, [page, checkDemoUserSignIn, setDemoUserSignIn])

  const normalizedSiteUrl = React.useMemo(() => {
    if (!siteUrl) return null
    return siteUrl.endsWith('/') ? siteUrl : `${siteUrl}/`
  }, [])

  React.useEffect(() => {
    if (!open || effectivePage !== Pages.DemoEmailVerify) {
      return
    }

    setDemoDiagramTypeQuery(getDemoDiagramTypeQuery())
  }, [open, effectivePage])

  const iframeSrc = React.useMemo(() => {
    const target = new URL(buildLocalizedSiteUrl(siteUrl, effectivePage, locale), window.location.origin)
    if (effectivePage === Pages.DemoEmailVerify && demoDiagramTypeQuery) {
      new URLSearchParams(demoDiagramTypeQuery).forEach((value, key) => target.searchParams.set(key, value))
    }
    if (demoDiagramId.current) target.searchParams.set('return_url', demoCheckoutReturnUrl(window.location.origin, demoDiagramId.current))
    return target.href
  }, [effectivePage, demoDiagramTypeQuery, locale])


  const iframeDisplaySrc = React.useMemo(() => {
    if (effectivePage !== Pages.SignIn || signInFrameNonce === 0) {
      return iframeSrc
    }
    const sep = iframeSrc.includes('?') ? '&' : '?'
    return `${iframeSrc}${sep}_createlyAiFrame=${signInFrameNonce}`
  }, [effectivePage, iframeSrc, signInFrameNonce])

  React.useEffect(() => {
    const wasClosed = !prevOpenRef.current && open
    prevOpenRef.current = open
    if (wasClosed && effectivePage === Pages.SignIn) {
      setSignInFrameNonce(0)
    }
  }, [open, effectivePage])

  const allowedOrigin = React.useMemo(() => {
    try {
      return new URL(iframeSrc).origin
    } catch {
      return undefined
    }
  }, [iframeSrc])

  const closeDialog = React.useCallback(() => {
    setOpen(false)
    setDismissed(true)
  }, [])

  const updateOnboardingStatus = useMutation({
    mutationFn: async (data: OnboardingStatusData) => {
      const token = getCookie('gravity_token');
      if (!token) throw new Error('No auth token found');

      return onboardingService.updateOnboardingStatus(data, token);
    }
  });

  const finishOnboarding = useCallback(async () => {
    const url = new URL(window.location.href);
    const userId = getGravityUserId();
    const diagramId = pendingDemoContinuation(url, userId);
    if (!diagramId || !userId) {
      closeDialog();
      const destination = folderFirstOnboardingDestination(url);
      if (destination) window.location.assign(destination);
      return;
    }
    setOpen(true);
    setPreparingWork(true);
    setRecovery(null);
    try {
      const folderId = await queryClient.fetchQuery({
        queryKey: ['demo-continuation', userId, diagramId],
        staleTime: 0,
        queryFn: () => resolveDemoContinuationFolder(authenticatedFetch, diagramId, userId),
      });
      const destination = new URL(`/dashboard${buildFolderPath(folderId)}`, url.origin);
      destination.searchParams.set('continueDiagram', diagramId);
      destination.searchParams.set('dashboard_ui', 'new');
      window.location.assign(destination.href);
    } catch (error) {
      logger.error('Failed to resolve demo continuation', error);
      setRecovery({ retry: () => { void finishOnboarding(); } });
    } finally {
      setPreparingWork(false);
    }
  }, [authenticatedFetch, closeDialog, queryClient]);

  const automaticContinuationStarted = React.useRef(false);
  React.useEffect(() => {
    const url = new URL(window.location.href);
    if (externalOnly || isEmbedded || automaticContinuationStarted.current || !onboardingStatus?.onboardingProgress
      || !/^\/dashboard(?:\/(?:recent|myorg|personal))?\/?$/.test(url.pathname)
      || !pendingDemoContinuation(url, getGravityUserId())) return;
    automaticContinuationStarted.current = true;
    void finishOnboarding();
  }, [externalOnly, isEmbedded, onboardingStatus, finishOnboarding]);

  const updateOnboardingAndClose = useCallback((data: OnboardingStatusData) => {
    if (Object.keys(data).length === 0) {
      void finishOnboarding();
      return;
    }
    updateOnboardingStatus.mutate(data, {
      onSuccess: () => { void finishOnboarding(); },
      onError: (error) => {
        logger.error('Failed to update onboarding status', error);
        toast.error('We could not save your onboarding selection. Please try again.');
      }
    });
  }, [finishOnboarding, updateOnboardingStatus]);

  const handleSignUp = React.useCallback(() => {
    const newPlan = getGravityUserPlan()
    if (newPlan?.startsWith('team')) {
      closeDialog()
    } else {
      setPage(Pages.Plans)
    }
  }, [closeDialog])

  const redirectToPlanPage = React.useCallback((planPath: string) => {
    if (typeof window === 'undefined') {
      return
    }

    const base = normalizedSiteUrl ?? (window.location.origin.endsWith('/')
      ? window.location.origin
      : `${window.location.origin}/`)
    const formattedBase = base.endsWith('/') ? base : `${base}/`
    const sanitizedPath = planPath.startsWith('/') ? planPath.slice(1) : planPath

    const destination = new URL(`${formattedBase}${sanitizedPath}`)
    if (demoDiagramId.current) destination.searchParams.set('return_url', demoCheckoutReturnUrl(window.location.origin, demoDiagramId.current))
    closeDialog()
    window.location.assign(destination.href)
  }, [closeDialog, normalizedSiteUrl])

  const openAppInNewWindow = React.useCallback(() => {
    window.open(baseUrl, '_blank')
  }, [])

  const openOnboarding = React.useCallback((detail?: { page?: string, fromUpgrade?: boolean }) => {    
    externalTriggerRef.current = true
    setDemoUserSignIn(false)
    setDismissed(false)
    setContentSize({})
    setOpen(true)

    const requestedPage = detail?.page

    const fromUpgradeFlag = detail?.fromUpgrade;
    setFromUpgrade(Boolean(fromUpgradeFlag))
    
    Tracker.track('conversion.signup.prompt', { page: requestedPage, fromUpgrade: fromUpgradeFlag });
    
    if (requestedPage === Pages.SignUp) {
      setPage(Pages.SignUp)
      return
    }
    if (requestedPage === Pages.Plans) {
      setPage(Pages.Plans)
      return
    }
    if (requestedPage === Pages.SignIn) {
      setSignInFrameNonce(0)
      setPage(Pages.SignIn)
      return
    }

    setPage(Pages.SignUp)
  }, [setDemoUserSignIn])

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const listener = (event: Event) => {
      const customEvent = event as CustomEvent<{ page?: string } | undefined>
      openOnboarding(customEvent.detail)
    }

    window.addEventListener(OPEN_ONBOARDING_EVENT, listener as EventListener)
    return () => window.removeEventListener(OPEN_ONBOARDING_EVENT, listener as EventListener)
  }, [openOnboarding])

  const handlers = React.useMemo<Record<string, PostMessageHandler>>(() => ({
    [ReceivePostMessageEvents.DEMO_EMAIL_CONTINUE]: () => {
      closeDialog()
    },
    [ReceivePostMessageEvents.EMAIL_VERIFY_SUCCESS]: (payload) => {
      const data = payload as EmailVerificationPayload | undefined
      Tracker.track('conversion.signup.complete', { method: 'email_verify', plan: data?.plan });
      if (data && typeof data.plan === 'string' && data.plan) {
        void handleAuthSuccess(() => redirectToPlanPage(data.plan!))
        return
      }
      void handleAuthSuccess(handleSignUp)
    },
    [ReceivePostMessageEvents.SIGN_UP_SUCCESS]: () => {
      Tracker.track('conversion.signup.complete', { method: 'signup' });
      void handleAuthSuccess(handleSignUp)
    },
    [ReceivePostMessageEvents.TRANSFER_DEMO_DIAGRAM]: () => {
      void handleAuthSuccess()
    },
    [ReceivePostMessageEvents.GRAVITY_TOKEN]: (payload) => {
      if (isDevopsPreviewHost(window.location.hostname)) {
        const token = (payload as { token?: unknown } | undefined)?.token
        const claims = typeof token === 'string' ? getGravityTokenClaimsFromToken(token) : null
        if (typeof token !== 'string' || typeof claims?.uid !== 'string' || !claims.uid ||
          typeof claims.status !== 'string' || claims.status === DEMO_STATUS ||
          typeof claims.exp !== 'number' || claims.exp <= Date.now() / 1000) return
        useAuthStore.getState().setAuth({
          id: claims.uid,
          email: '',
          name: '',
          role: 'user',
          createdAt: new Date(),
        }, token)
      }
      setHasReceivedGravityToken(true)
      syncSessionFromBrowser()
      if (pendingPreviewSignInRef.current) {
        pendingPreviewSignInRef.current = false
        void handleAuthSuccess(closeDialog)
      }
    },
    [ReceivePostMessageEvents.SIGN_UP_SIZE_CHANGE]: (payload) => {
      const data = (payload as { width?: number; height?: number }) || {}
      setContentSize({
        width: typeof data.width === 'number' || typeof data.width === 'string' ? data.width : undefined,
        height: typeof data.height === 'number' || typeof data.height === 'string' ? data.height : undefined,
      })
    },
    [ReceivePostMessageEvents.SIGN_UP_CLOSE]: () => {
      closeDialog()
    },
    [ReceivePostMessageEvents.SIGN_IN_SUCCESS]: () => {
      Tracker.track('conversion.signup.complete', { method: 'signin' });
      if (isDevopsPreviewHost(window.location.hostname) &&
        initialUserStatus.current === DEMO_STATUS && getGravityUserStatus() === DEMO_STATUS) {
        pendingPreviewSignInRef.current = true
        return
      }
      void handleAuthSuccess(closeDialog)
    },
    // User has tried to sign up using an existing email, show login page on new window
    [ReceivePostMessageEvents.SIGN_UP_FAIL]: () => {
      setDemoUserSignIn(true)
      openAppInNewWindow()
    },
    [ReceivePostMessageEvents.CONTINUE_FREE]: () => {
      void handleAuthSuccess(() => window.location.reload())
    },
    [ReceivePostMessageEvents.RELOAD_APP]: () => {
      void handleAuthSuccess(() => window.location.reload())
    },
    [ReceivePostMessageEvents.PURCHASE_STARTED]: () => {
      setPage(Pages.PurchaseStarted)
    },
    [ReceivePostMessageEvents.ONBOARDING_SUCCESS]: (payload) => {
      const data = (payload ?? {}) as PhoenixOnboardingSuccessPayload
      const purpose = resolveOnboardingPurpose(data)
      updateOnboardingAndClose({
        intendedUsage: data.usage,
        industry: data.industry,
        jobTitle: data.role,
        purpose,
        onboardingProgress: data.onboardingProgress ?? 'complete',
      })
    },
    [ReceivePostMessageEvents.ONBOARDING_ERROR]: () => {
      closeDialog()
    },
  }), [closeDialog, handleSignUp, redirectToPlanPage, setDemoUserSignIn, openAppInNewWindow, handleAuthSuccess, syncSessionFromBrowser, updateOnboardingAndClose])

  usePostMessageRouter({
    enabled: open,
    allowedOrigin,
    requiredSource: 'creately',
    iframeRef,
    handlers,
  })

  const isOnboardingIframe = page === Pages.Onboarding
  const canDismissCurrentPage = effectivePage !== Pages.DemoEmailVerify && isShareLinkLoginContext && !hasReceivedGravityToken

  if (!open || isEmbedded) {
    return null
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && canDismissCurrentPage) {
          closeDialog()
        }
      }}
    >
      <DialogContent
        className={cn(
          'p-0 z-[1001] [&_[data-slot=dialog-close]]:z-20',
          isOnboardingIframe
            ? 'w-[min(760px,calc(100vw-32px))] h-[min(352px,calc(100vh-32px))] max-h-[calc(100vh-32px)] sm:max-w-[760px] transition-[height] duration-200'
            : isCompact
            ? 'w-[600px] h-[600px] max-h-[600px] sm:max-w-[600px]'
            : 'w-[calc(100vw-60px)] h-[calc(100vh-50px)] sm:max-w-[1280px]'
        )}
        overlayClassName="z-[1000]"
        showCloseButton={canDismissCurrentPage}
        onInteractOutside={(event) => {
          if (!canDismissCurrentPage) {
            event.preventDefault()
          }
        }}
        onEscapeKeyDown={(event) => {
          if (!canDismissCurrentPage) {
            event.preventDefault()
          }
        }}
        style={{
          width: contentSize.width,
          height: contentSize.height,
        }}
      >
        <VisuallyHidden>
          <DialogTitle>Onboarding</DialogTitle>
        </VisuallyHidden>
        {(preparingWork || recovery) && (
          <div className="absolute inset-0 z-10 bg-background flex flex-col items-center justify-center gap-4 p-8 text-center" role="status">
            <p className="text-lg font-semibold">{preparingWork ? 'Getting your diagram ready…' : 'We couldn’t bring your diagram back yet'}</p>
            <p className="text-sm text-muted-foreground">{preparingWork ? 'Please wait while we prepare your existing work.' : 'Please retry to continue from your existing diagram.'}</p>
            {recovery && !preparingWork && <Button onClick={recovery.retry}>Try again</Button>}
          </div>
        )}
        <div className="flex flex-1">
          <iframe
            ref={iframeRef}
            src={iframeDisplaySrc}
            title="Onboarding"
            className={cn('w-full h-full')}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
